import { Component, Input } from '@angular/core';
import { IconSize } from '../../core-data-model/IconSize';
import { IconType } from '../../core-data-model/IconType';
import { DateFormatUtility } from '../../core-utilities/DateFormat.utility';
import { Util } from '../../core-utilities/Util';
import { TranslationService } from '../../translations';

export enum FooterStyle {
    None = 1,
    Classic = 2,
    Modern = 3,
    ModernCopyright = 4
}

export type ReportSkeletonHeaderIconConfig = {
    cssClass?: string;
    iconType?: IconType;
    iconSize?: IconSize;
};

@Component({
    selector: 'report-skeleton',
    template: `
        <div class="reportSkeleton">
            <div
                class="reportSkeleton_page {{
                    isLastPage ? 'reportSkeleton_lastPage' : ''
                }}"
            >
                @if (showHeader) {
                    <div
                        [ngClass]="headerCSS"
                        class="reportSkeleton_page_header"
                    >
                        <ng-content select="[header]"></ng-content>
                        <abstract-icon
                            [ngClass]="getHeaderIconConfig().cssClass"
                            [iconType]="getHeaderIconConfig().iconType"
                            [iconSize]="getHeaderIconConfig().iconSize"
                        >
                        </abstract-icon>
                    </div>
                }
                <div class="reportSkeleton_page_content" [ngStyle]="contentCss">
                    <ng-content select="[body]"></ng-content>
                </div>
                <div
                    class="reportSkeleton_page_footer"
                    [ngClass]="{
                        'reportSkeleton_page_footer--noBackground':
                            footerStyle === FooterStyle.ModernCopyright
                    }"
                >
                    @if (footerStyle === FooterStyle.Classic) {
                        <span [innerHTML]="getFooterText()"></span>
                        @if (totalPageCount > 1) {
                            <span><b></b> / {{ totalPageCount }}</span>
                        }
                        <abstract-icon
                            [iconType]="IconType.PiLogoWhite"
                            [iconSize]="IconSize.Large"
                        ></abstract-icon>
                    }
                    @if (footerStyle === FooterStyle.Modern) {
                        <abstract-icon
                            [iconType]="IconType.PiLogoWhiteWithName"
                            style="margin-left: 25px;"
                        >
                        </abstract-icon>
                        <span style="line-height:38px;">
                            {{ footerName }}
                            <span
                                style="margin:0 45px;font-weight:normal;"
                                class="largeText"
                                ><b></b
                            ></span>
                        </span>
                    }
                    @if (footerStyle === FooterStyle.ModernCopyright) {
                        <span style="margin-top:40px;margin-left:40px;">{{
                            getCopyRightText()
                        }}</span>
                        <b style="color:white"></b>
                        <!--Done in order to handle the page numbers-->
                        <abstract-icon
                            [iconType]="IconType.PiLogoRedWithName"
                            style="margin-bottom: 20px;margin-right: 50px"
                        >
                        </abstract-icon>
                    }
                </div>
            </div>
        </div>
    `,
    standalone: false
})
export class ReportSkeletonComponent {
    // all inputs are intended to only be set once and not updated.
    @Input()
    footerDate: string;
    @Input()
    footerName: string;
    @Input()
    totalPageCount = 0;
    @Input()
    isLastPage = false;
    @Input()
    header: string;
    @Input()
    headerCSS: string;
    @Input()
    headerIconConfig: ReportSkeletonHeaderIconConfig;
    @Input()
    contentCss: string;
    @Input()
    footerStyle: FooterStyle = FooterStyle.Classic;
    @Input()
    showHeader = true;

    FooterStyle = FooterStyle;
    IconType = IconType;
    IconSize = IconSize;

    defaultPILogoIcon: ReportSkeletonHeaderIconConfig = {
        iconType: IconType.PiLogoWhite,
        iconSize: IconSize.EDGECASE_ReportHeaderLogo,
        cssClass: 'pi-logo'
    };

    constructor(
        public TR: TranslationService,
        public dateFormatUtil: DateFormatUtility,
        public util: Util
    ) {}

    getHeaderIconConfig(): ReportSkeletonHeaderIconConfig {
        const settings = Object.assign(
            {},
            this.defaultPILogoIcon,
            this.headerIconConfig
        );
        return settings;
    }

    getFooterText(): string {
        const reportDate: Date = this.footerDate
            ? new Date(this.footerDate)
            : new Date();
        if (this.footerDate && this.footerName) {
            return `${this.footerName} | ${this.dateFormatUtil.transformDate(
                reportDate
            )}`;
        } else {
            return (
                this.footerName || this.dateFormatUtil.transformDate(reportDate)
            );
        }
    }

    getCopyRightText() {
        return this.util.strings.formatString(
            this.TR.key('text.placard.footer.copyright'),
            new Date().getFullYear()
        );
    }
}
